import { useEffect, useState } from "react";
import { CalendarDays, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

const formatDay = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function AdminAgenda() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(null);
  const [slots, setSlots] = useState(null);
  const [form, setForm] = useState({ date: "", time: "10h00 - 11h30", capacity: 8 });
  const [saving, setSaving] = useState(false);
  const [recurrence, setRecurrence] = useState({ weekday: "5", times: "10h00 - 11h30, 15h00 - 16h30", capacity: 8, weeks: 8 });
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => setSelected((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
  const bulkDelete = async () => {
    if (!selected.length || !window.confirm(`Supprimer ${selected.length} date(s) ? Les dates déjà réservées seront conservées.`)) return;
    try {
      const { data } = await api.post("/admin/slots/bulk-delete", { slot_ids: selected });
      toast.success(data.message);
      setSelected([]);
      loadSlots();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/admin/products").then(({ data }) => {
      const bookable = data.filter((p) => p.bookable);
      setProducts(bookable);
      if (bookable.length > 0) setProductId((cur) => cur || bookable[0].product_id);
    }).catch(() => {});
  }, []);

  const loadSlots = () =>
    productId && api.get(`/admin/slots?product_id=${productId}`).then(({ data }) => setSlots(data)).catch(() => setSlots([]));

  useEffect(() => {
    setSlots(null);
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setSelected([]);
  }, [productId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!productId) return;
    setSaving(true);
    try {
      await api.post("/admin/slots", { product_id: productId, ...form, capacity: parseInt(form.capacity, 10) || 1 });
      toast.success("Créneau ajouté — visible sur la fiche produit");
      setForm((f) => ({ ...f, date: "" }));
      loadSlots();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le créneau du ${formatDay(s.date)} (${s.time}) ?`)) return;
    try {
      await api.delete(`/admin/slots/${s.slot_id}`);
      toast.success("Créneau supprimé");
      loadSlots();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleRecurrence = async (e) => {
    e.preventDefault();
    if (!productId) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/slots/recurrence", {
        product_id: productId,
        weekday: parseInt(recurrence.weekday, 10),
        times: recurrence.times,
        capacity: parseInt(recurrence.capacity, 10) || 8,
        weeks: parseInt(recurrence.weeks, 10) || 1,
      });
      toast.success(`${data.created} créneau(x) créé(s)${data.skipped ? ` — ${data.skipped} existaient déjà` : ""}`);
      loadSlots();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const WEEKDAYS = [
    { value: "0", label: "Lundi" },
    { value: "1", label: "Mardi" },
    { value: "2", label: "Mercredi" },
    { value: "3", label: "Jeudi" },
    { value: "4", label: "Vendredi" },
    { value: "5", label: "Samedi" },
    { value: "6", label: "Dimanche" },
  ];

  return (
    <div data-testid="admin-agenda" className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-bark">Agenda</h1>
        <p className="mt-1 text-sm text-bark-soft">
          Définissez ici les créneaux proposés aux clients pour les produits avec réservation (visites de ruche…).
        </p>
      </div>

      {products.length === 0 ? (
        <p data-testid="agenda-no-product" className="rounded-2xl border border-amber-800/15 bg-white p-8 text-center font-serif text-lg text-bark-soft shadow-sm">
          Aucun produit réservable. Activez « Produit avec réservation » sur une fiche produit (ex. Visite du Rucher).
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="ag-product" className="text-sm font-medium text-bark">Produit :</label>
            <select
              id="ag-product"
              data-testid="ag-product"
              value={productId || ""}
              onChange={(e) => setProductId(e.target.value)}
              className="rounded-full border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep"
            >
              {products.map((p) => (
                <option key={p.product_id} value={p.product_id}>{p.name}</option>
              ))}
            </select>
          </div>

          <form data-testid="slot-form" onSubmit={handleAdd} className="flex flex-wrap items-end gap-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <div>
              <label htmlFor="sl-date" className="mb-1.5 block text-sm font-medium text-bark">Date *</label>
              <input id="sl-date" data-testid="sl-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label htmlFor="sl-time" className="mb-1.5 block text-sm font-medium text-bark">Horaire *</label>
              <input id="sl-time" data-testid="sl-time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputClass} placeholder="10h00 - 11h30" />
            </div>
            <div>
              <label htmlFor="sl-capacity" className="mb-1.5 block text-sm font-medium text-bark">Places *</label>
              <input id="sl-capacity" data-testid="sl-capacity" type="number" min="1" max="100" required value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={`${inputClass} w-28`} />
            </div>
            <button
              data-testid="sl-submit"
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
            >
              <Plus size={15} /> {saving ? "Ajout…" : "Ajouter le créneau"}
            </button>
          </form>

          <div className="rounded-2xl border border-amber-800/15 bg-white shadow-sm">
            <button
              data-testid="recurrence-toggle"
              onClick={() => setRecurrenceOpen((v) => !v)}
              className="flex w-full items-center justify-between px-6 py-4 text-left"
            >
              <span className="font-serif text-lg font-semibold text-bark">Créneaux récurrents (ex. tous les samedis)</span>
              <span className="text-sm font-medium text-amber-deep">{recurrenceOpen ? "Masquer" : "Afficher"}</span>
            </button>
            {recurrenceOpen && (
              <form data-testid="recurrence-form" onSubmit={handleRecurrence} className="flex flex-wrap items-end gap-4 border-t border-amber-900/10 px-6 pb-6 pt-4">
                <div>
                  <label htmlFor="rc-weekday" className="mb-1.5 block text-sm font-medium text-bark">Jour de la semaine</label>
                  <select id="rc-weekday" data-testid="rc-weekday" value={recurrence.weekday} onChange={(e) => setRecurrence({ ...recurrence, weekday: e.target.value })} className={inputClass}>
                    {WEEKDAYS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="rc-times" className="mb-1.5 block text-sm font-medium text-bark">Horaires (séparés par des virgules)</label>
                  <input id="rc-times" data-testid="rc-times" required value={recurrence.times} onChange={(e) => setRecurrence({ ...recurrence, times: e.target.value })} className={`${inputClass} w-72`} placeholder="10h00 - 11h30, 15h00 - 16h30" />
                </div>
                <div>
                  <label htmlFor="rc-capacity" className="mb-1.5 block text-sm font-medium text-bark">Places</label>
                  <input id="rc-capacity" data-testid="rc-capacity" type="number" min="1" max="100" required value={recurrence.capacity} onChange={(e) => setRecurrence({ ...recurrence, capacity: e.target.value })} className={`${inputClass} w-24`} />
                </div>
                <div>
                  <label htmlFor="rc-weeks" className="mb-1.5 block text-sm font-medium text-bark">Semaines à générer</label>
                  <input id="rc-weeks" data-testid="rc-weeks" type="number" min="1" max="16" required value={recurrence.weeks} onChange={(e) => setRecurrence({ ...recurrence, weeks: e.target.value })} className={`${inputClass} w-24`} />
                </div>
                <button
                  data-testid="rc-submit"
                  type="submit"
                  disabled={generating}
                  className="flex items-center gap-2 rounded-full bg-sage px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-sage/80 active:scale-95 disabled:opacity-60"
                >
                  <CalendarDays size={15} /> {generating ? "Génération…" : "Générer"}
                </button>
                <p className="w-full text-xs text-stone-mute">Les créneaux déjà existants (même date et horaire) sont ignorés.</p>
              </form>
            )}
          </div>

          {!slots ? (
            <p data-testid="slots-loading" className="py-10 text-center font-serif text-xl text-bark-soft">Chargement…</p>
          ) : slots.length === 0 ? (
            <p data-testid="slots-empty" className="py-10 text-center font-serif text-xl text-bark-soft">Aucun créneau pour le moment.</p>
          ) : (
            <>
            <div data-testid="slots-bulk-bar" className="mb-3 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-800/15 bg-white px-4 py-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-bark">
                <input type="checkbox" data-testid="slots-select-all" checked={selected.length === slots.length && slots.length > 0} onChange={(e) => setSelected(e.target.checked ? slots.map((x) => x.slot_id) : [])} className="h-4 w-4 accent-amber-deep" />
                Tout sélectionner ({slots.length})
              </label>
              <button data-testid="slots-bulk-delete" onClick={bulkDelete} disabled={!selected.length} className="ml-auto flex items-center gap-1.5 rounded-full border border-terra/30 px-4 py-2 text-sm font-medium text-terra hover:bg-terra/10 disabled:opacity-40">
                <Trash2 size={14} /> Supprimer la sélection{selected.length ? ` (${selected.length})` : ""}
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {slots.map((s) => {
                const remaining = s.capacity - (s.booked || 0);
                return (
                  <div key={s.slot_id} data-testid={`slot-row-${s.slot_id}`} className={`flex items-center gap-4 rounded-2xl border bg-white p-5 shadow-sm ${selected.includes(s.slot_id) ? "border-terra/50 ring-1 ring-terra/30" : "border-amber-800/15"}`}>
                    <input type="checkbox" data-testid={`slot-select-${s.slot_id}`} checked={selected.includes(s.slot_id)} onChange={() => toggleSel(s.slot_id)} aria-label="Sélectionner" className="h-4 w-4 shrink-0 accent-amber-deep" />
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sage-light text-sage">
                      <CalendarDays size={22} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-lg font-semibold capitalize text-bark">{formatDay(s.date)}</p>
                      <p className="text-sm text-bark-soft">{s.time}</p>
                      <p data-testid={`slot-remaining-${s.slot_id}`} className={`mt-1 flex items-center gap-1.5 text-xs font-medium ${remaining === 0 ? "text-terra" : "text-sage"}`}>
                        <Users size={12} /> {remaining === 0 ? "Complet" : `${remaining} place(s) restantes sur ${s.capacity}`}
                      </p>
                    </div>
                    <button
                      data-testid={`slot-delete-${s.slot_id}`}
                      onClick={() => handleDelete(s)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                      aria-label="Supprimer le créneau"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
